import { TrafikenConfig } from '../Map/TrafikenConfig';
import {
    type JourneysDetailsReferenceDetailsGetRequest,
    type JourneysGetRequest,
    type VTApiPlaneraResaWebV4ModelsJourneyDetailsJourneyDetailsApiModel,
    type VTApiPlaneraResaWebV4ModelsJourneysGetJourneysResponse,
    type VTApiPlaneraResaWebV4ModelsLocationsGetLocationsResponse,
} from './VasttrafikApiClient.types';

const tokenReuseWindowMs = 1000;
let cachedAccessToken: string | undefined;
let tokenReuseUntil = 0;
let tokenRequest: Promise<string> | undefined;

interface IVasttrafikApiClient {
    getJourneys(requestParameters: JourneysGetRequest): Promise<VTApiPlaneraResaWebV4ModelsJourneysGetJourneysResponse>;
    getJourneyDetails(
        requestParameters: JourneysDetailsReferenceDetailsGetRequest,
    ): Promise<VTApiPlaneraResaWebV4ModelsJourneyDetailsJourneyDetailsApiModel>;
    getLocationsByText(query: string): Promise<VTApiPlaneraResaWebV4ModelsLocationsGetLocationsResponse>;
    getLocationsByCoordinates(latitude: number, longitude: number): Promise<VTApiPlaneraResaWebV4ModelsLocationsGetLocationsResponse>;
}

export class VasttrafikApiClient implements IVasttrafikApiClient {
    #serviceUrl: string;

    constructor(serviceUrl: string) {
        this.#serviceUrl = serviceUrl;
    }

    public async getJourneys(requestParameters: JourneysGetRequest): Promise<VTApiPlaneraResaWebV4ModelsJourneysGetJourneysResponse> {
        return await this.get<VTApiPlaneraResaWebV4ModelsJourneysGetJourneysResponse>(
            `${this.#serviceUrl}/journeys?${this.getQueryString(requestParameters)}`,
        );
    }

    public async getJourneyDetails(
        requestParameters: JourneysDetailsReferenceDetailsGetRequest,
    ): Promise<VTApiPlaneraResaWebV4ModelsJourneyDetailsJourneyDetailsApiModel> {
        const detailsReference = requestParameters.detailsReference;
        delete requestParameters.detailsReference;

        return await this.get<VTApiPlaneraResaWebV4ModelsJourneyDetailsJourneyDetailsApiModel>(
            `${this.#serviceUrl}/journeys/${detailsReference}/details?${this.getQueryString(requestParameters)}`,
        );
    }

    public async getLocationsByText(query: string): Promise<VTApiPlaneraResaWebV4ModelsLocationsGetLocationsResponse> {
        return await this.get<VTApiPlaneraResaWebV4ModelsLocationsGetLocationsResponse>(
            `${this.#serviceUrl}/locations/by-text?${this.getQueryString({ q: query })}`,
        );
    }

    public async getLocationsByCoordinates(latitude: number, longitude: number): Promise<VTApiPlaneraResaWebV4ModelsLocationsGetLocationsResponse> {
        return await this.get<VTApiPlaneraResaWebV4ModelsLocationsGetLocationsResponse>(
            `${this.#serviceUrl}/locations/by-coordinates?${this.getQueryString({ latitude, longitude })}`,
        );
    }

    private async get<T>(url: string): Promise<T> {
        const token = await this.fetchAuthToken();

        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Accept-Language': 'sv',
            },
        });

        if (!response.ok) {
            const error = await response.json();
            console.error('Failed to get data from Västtrafik API', error);
            throw new Error('Failed to get data from Västtrafik API');
        }

        return await response.json();
    }

    private getQueryString(parameters: unknown): string {
        const queryString = Object.keys(parameters)
            .map((key) => {
                if (Array.isArray(parameters[key])) {
                    return parameters[key].map((value) => `${key}=${encodeURIComponent(value)}`).join('&');
                }
                return `${key}=${encodeURIComponent(parameters[key])}`;
            })
            .join('&');

        return queryString;
    }

    private fetchAuthToken(): Promise<string> {
        if (cachedAccessToken !== undefined && Date.now() < tokenReuseUntil) {
            return Promise.resolve(cachedAccessToken);
        }

        if (!tokenRequest) {
            const request = fetch(`${TrafikenConfig.vasttrafikTokenUrl}`)
                .then((response) => {
                    if (!response.ok) {
                        throw new Error('Failed to get Västtrafik access token');
                    }

                    return response.text();
                })
                .then((accessToken) => {
                    cachedAccessToken = accessToken;
                    tokenReuseUntil = Date.now() + tokenReuseWindowMs;
                    return accessToken;
                });

            tokenRequest = request.finally(() => {
                tokenRequest = undefined;
            });
        }

        return tokenRequest;
    }
}
